import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAdminAuth } from '@/context/AdminAuthContext';
import type { components } from '@/api/schema';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-error';
import { useTranslation } from 'react-i18next';
import { intlLocale } from './adminLocale';

/** Ruxsat etilgan bo'lib to'lash muddatlari */
const TENORS = [3, 6, 12, 18, 24];

type MarkupRule = components['schemas']['MarkupRuleResource'];

const emptyForm = {
  tenor_months: 6,
  markup_percent: 12,
  valid_from: new Date().toISOString().split('T')[0],
  is_active: true,
};

export default function AdminPricing() {
  const { t } = useTranslation();
  const { admin } = useAdminAuth();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Global ustamalarni faqat Platform owner o'zgartiradi.
  const canEdit = admin?.role === 'platform_owner';

  const { data, isLoading: loading } = useQuery({ queryKey: ['/admin/markup-rules'] });
  const rules: MarkupRule[] = [...(data?.data ?? [])].sort((a, b) => a.tenor_months - b.tenor_months);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['/admin/markup-rules'] });

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (rule: MarkupRule) => {
    setEditingId(rule.id);
    setForm({
      tenor_months: rule.tenor_months,
      markup_percent: Number(rule.markup_percent),
      valid_from: rule.valid_from.slice(0, 10),
      is_active: rule.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        tenor_months: form.tenor_months,
        markup_percent: form.markup_percent,
        valid_from: form.valid_from,
        is_active: form.is_active,
      };

      if (editingId) {
        const { error } = await apiClient.PATCH('/admin/markup-rules/{markupRule}', {
          params: { path: { markupRule: editingId } },
          body: payload,
        });
        if (error) throw error;
        toast.success(t('adm_pricing.updated'));
      } else {
        const { error } = await apiClient.POST('/admin/markup-rules', { body: payload });
        if (error) throw error;
        toast.success(t('adm_pricing.created'));
      }
      setDialogOpen(false);
      invalidate();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const { error } = await apiClient.DELETE('/admin/markup-rules/{markupRule}', {
        params: { path: { markupRule: deleteId } },
      });
      if (error) throw error;
      toast.success(t('adm_pricing.deleted'));
      setDeleteId(null);
      invalidate();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally { setDeleting(false); }
  };


  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('adm_pricing.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('adm_pricing.subtitle')}</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} size="sm">
            <Plus size={16} className="mr-1" /> {t('adm_pricing.newRule')}
          </Button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-5 gap-4">
        {TENORS.map(tenor => {
          const globalRule = rules.find(r => r.tenor_months === tenor && r.is_active);
          return (
            <Card key={tenor}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t('adm_pricing.tenorTitle', { count: tenor })}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{globalRule ? `${Number(globalRule.markup_percent)}%` : t('adm_pricing.notSet')}</div>
                <p className="text-xs text-muted-foreground">{t('adm_pricing.globalMarkup')}</p>

              </CardContent>
            </Card>
          );
        })}
      </div>

      {loading ? (
        <div className="text-muted-foreground py-8">{t('adm_common.loading')}</div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('adm_pricing.term')}</TableHead>
                <TableHead>{t('adm_pricing.markupPct')}</TableHead>
                <TableHead>{t('adm_pricing.startDate')}</TableHead>
                <TableHead>{t('adm_pricing.status')}</TableHead>
                {canEdit && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">{t('adm_pricing.empty')}</TableCell></TableRow>
              ) : (
                rules.map(rule => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-medium">{t('adm_pricing.months', { count: rule.tenor_months })}</TableCell>
                    <TableCell className="font-mono font-bold">{Number(rule.markup_percent)}%</TableCell>

                    <TableCell className="text-xs">{new Date(rule.valid_from).toLocaleDateString(intlLocale())}</TableCell>
                    <TableCell>
                      <Badge variant={rule.is_active ? 'default' : 'outline'}>
                        {rule.is_active ? t('adm_pricing.active') : t('adm_pricing.inactive')}
                      </Badge>
                    </TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(rule)}>
                            <Pencil size={14} />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeleteId(rule.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? t('adm_pricing.editRule') : t('adm_pricing.newRule')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('adm_pricing.term')}</Label>
                <Select value={String(form.tenor_months)} onValueChange={v => setForm({ ...form, tenor_months: Number(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TENORS.map(tn => <SelectItem key={tn} value={String(tn)}>{t('adm_pricing.months', { count: tn })}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('adm_pricing.markupPercentLabel')}</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={form.markup_percent}
                  onChange={e => setForm({ ...form, markup_percent: Number(e.target.value) })}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('adm_pricing.globalNote')}
            </p>

            <div className="space-y-2">
              <Label>{t('adm_pricing.startDate')}</Label>
              <Input type="date" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
              <Label>{t('adm_pricing.active')}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('adm_common.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? t('adm_common.saving') : t('adm_common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('adm_pricing.deleteRule')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('adm_pricing.deleteConfirm')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>{t('adm_common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? t('adm_pricing.deleting') : t('adm_pricing.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
